import { useRerender, useConst } from '@hmans/react-toolbox';
import { World, Tag } from 'miniplex';
import React, { useContext, forwardRef, useState, useImperativeHandle, useEffect, memo, useRef, cloneElement, createContext } from 'react';
import mergeRefs from 'react-merge-refs';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

var _excluded = ["entities"];
function createECS() {
  var world = new World();
  var EntityContext = /*#__PURE__*/createContext(null);
  /**
   * Returns the current entity context.
   */

  var useEntity = function useEntity() {
    return useContext(EntityContext);
  };
  /**
   * A React component to either create a new entity, or represent an existing entity so
   * it can be enhanced with additional components (see the <Component> component.)
   */


  var Entity = /*#__PURE__*/forwardRef(function (_ref, ref) {
    var existingEntity = _ref.entity,
        children = _ref.children;

    var _useState = useState(null),
        _useState2 = _slicedToArray(_useState, 2),
        entity = _useState2[0],
        setEntity = _useState2[1];
    /* Apply ref */


    useImperativeHandle(ref, function () {
      return entity;
    });
    /* If the entity was freshly created, manage its presence in the ECS world. */

    useEffect(function () {
      var entity = existingEntity !== null && existingEntity !== void 0 ? existingEntity : world.createEntity();
      setEntity(entity);
      return function () {
        if (!existingEntity) world.destroyEntity(entity);
        setEntity(null);
      };
    }, []);
    /* Provide a context with the entity so <Component> components can be wired up. */

    return entity && /*#__PURE__*/React.createElement(EntityContext.Provider, {
      value: entity
    }, typeof children === "function" ? children(entity) : children);
  });
  var MemoizedEntity = /*#__PURE__*/memo(Entity, function (a, b) {
    return a.entity === b.entity;
  });
  /**
   * Takes an array of entities and renders the inner JSX (or function) once per entity,
   * memoizing the results.
   */

  var Entities = function Entities(_ref2) {
    var entities = _ref2.entities,
        props = _objectWithoutProperties(_ref2, _excluded);

    return /*#__PURE__*/React.createElement(React.Fragment, null, entities.map(function (entity) {
      return /*#__PURE__*/React.createElement(MemoizedEntity, _extends({
        entity: entity,
        key: entity.__miniplex.id
      }, props));
    }));
  };

  function ManagedEntities(_ref3) {
    var _ref3$initial = _ref3.initial,
        initial = _ref3$initial === void 0 ? 0 : _ref3$initial,
        tag = _ref3.tag,
        children = _ref3.children;

    var _useArchetype = useArchetype(tag),
        entities = _useArchetype.entities;

    useEffect(function () {
      /* When firing up, create the requested number of entities. */
      for (var i = 0; i < initial; i++) {
        world.createEntity(_defineProperty({}, tag, Tag));
      }
      /* When shutting down, purge all entities in this collection. */


      return function () {
        for (var _i = entities.length; _i > 0; _i--) {
          world.destroyEntity(entities[_i - 1]);
        }
      };
    }, [tag, initial]);
    return /*#__PURE__*/React.createElement(Entities, {
      entities: entities,
      children: children
    });
  }
  /**
   * Declaratively add a component to an entity.
   */


  function Component(_ref4) {
    var name = _ref4.name,
        data = _ref4.data,
        children = _ref4.children;
    var entity = useEntity();
    var ref = useRef(null);
    /* Warn the user that passing multiple children is not allowed. */

    if (children && Array.isArray(children)) {
      throw new Error("<Component> will only accept a single React child.");
    }

    useEffect(function () {
      world.addComponent(entity, _defineProperty({}, name, data !== null && data !== void 0 ? data : ref.current));
      return function () {
        if ("__miniplex" in entity) {
          world.removeComponent(entity, name);
        }
      };
    }, [entity, name, data]);
    var childElement = children && (typeof children === "function" ? children(entity) : children);
    return /*#__PURE__*/React.createElement(React.Fragment, null, childElement && /*#__PURE__*/cloneElement(childElement, {
      ref: mergeRefs([ref, childElement.ref])
    }));
  }
  /**
   * Return the entities of the specified archetype and subscribe this component
   * to it, making it re-render when entities are added to or removed from it.
   */


  function useArchetype() {
    for (var _len = arguments.length, query = new Array(_len), _key = 0; _key < _len; _key++) {
      query[_key] = arguments[_key];
    }

    var rerender = useRerender();
    var archetype = useConst(function () {
      return world.archetype.apply(world, query);
    });
    useEffect(function () {
      archetype.onEntityAdded.add(rerender);
      archetype.onEntityRemoved.add(rerender);
      /* We need to rerender at least once, because other effects might have set up
         new entities before we had a chance to register our listeners. */

      rerender();
      return function () {
        archetype.onEntityAdded.remove(rerender);
        archetype.onEntityRemoved.remove(rerender);
      };
    }, [archetype]);
    return archetype;
  }

  return {
    Component: Component,
    Entity: Entity,
    Entities: Entities,
    ManagedEntities: ManagedEntities,
    MemoizedEntity: MemoizedEntity,
    useArchetype: useArchetype,
    useEntity: useEntity,
    world: world
  };
}

export { createECS };
